import { createContext, useContext, type ReactNode } from "react"

import type { WorkspaceResource } from "@/types_api/backend/transport/list_workspaces_response"

export interface WorkspaceContextValue {
  currentWorkspace: WorkspaceResource | null
  /**
   * Whether the organization workspace catalog is still loading. The catalog
   * is not required on organization-global routes, so consumers there cannot
   * distinguish a not-yet-loaded catalog from a truly empty one without this
   * flag: an empty `workspaces` list alone proves nothing.
   */
  isWorkspaceCatalogLoading: boolean
  /**
   * The workspace segment from the canonical URL.
   *
   * This is the workspace ID (`WorkspaceResource.id`), not the slug.
   */
  routeWorkspaceId: string | null
  workspaces: readonly WorkspaceResource[]
}

export const EMPTY_WORKSPACE_CONTEXT_VALUE: WorkspaceContextValue = {
  currentWorkspace: null,
  isWorkspaceCatalogLoading: false,
  routeWorkspaceId: null,
  workspaces: [],
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({
  children,
  value,
}: {
  children: ReactNode
  value: WorkspaceContextValue
}) {
  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export const useOptionalWorkspaceContext = () => useContext(WorkspaceContext)

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error("useWorkspaceContext must be used within WorkspaceProvider")
  }
  return context
}
